/**
 * Client-side logger that sends log entries to Grafana via the Next.js API.
 * Use in React components and client-side code only.
 * Logs are batched and sent to POST /api/log, which forwards to Loki.
 *
 * Designed to be fire-and-forget and invisible:
 * - Uses sendBeacon when possible (non-blocking, can complete on page unload)
 * - Defers flush to next tick so the current call stack never waits on network
 * - keepalive fetch as fallback for large payloads (> ~60KB)
 *
 * Usage:
 *   import { clientLogger } from '@/lib/client-logger';
 *   clientLogger.info('User did X', { userId, action: 'click' });
 *   clientLogger.error('Something failed', { error: err.message });
 */

export type ClientLogLevel = 'info' | 'warn' | 'error' | 'debug';

interface QueuedLog {
  level: ClientLogLevel;
  message: string;
  context?: Record<string, unknown>;
  ts: number;
}

const BATCH_INTERVAL_MS = 2000;
const BATCH_MAX_SIZE = 10;
const API_PATH = '/api/log/';
/** sendBeacon has ~64KB limit in some browsers; stay under to be safe */
const SEND_BEACON_MAX_BYTES = 60_000;

let queue: QueuedLog[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function isClient(): boolean {
  return typeof window !== 'undefined';
}

function scheduleFlush(): void {
  if (!isClient() || queue.length === 0) return;
  if (flushTimer != null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, BATCH_INTERVAL_MS);
}

function sendLogsFireAndForget(body: string): void {
  const useSendBeacon =
    typeof navigator !== 'undefined' &&
    typeof navigator.sendBeacon === 'function' &&
    body.length <= SEND_BEACON_MAX_BYTES;

  if (useSendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon(API_PATH, blob);
    return;
  }

  fetch(API_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    // Fire-and-forget; avoid unhandled rejection
  });
}

function flush(): void {
  if (!isClient() || queue.length === 0) {
    flushTimer = null;
    return;
  }
  flushTimer = null;
  const toSend = queue.splice(0, BATCH_MAX_SIZE);
  const payloads = toSend.map(({ level, message, context }) => ({ level, message, context }));
  const body = JSON.stringify(payloads.length === 1 ? payloads[0] : { batch: payloads });

  sendLogsFireAndForget(body);

  if (queue.length > 0) scheduleFlush();
}

function enqueue(level: ClientLogLevel, message: string, context?: Record<string, unknown>): void {
  if (!isClient()) return;
  queue.push({ level, message, context, ts: Date.now() });
  if (queue.length >= BATCH_MAX_SIZE) {
    if (flushTimer != null) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    // Defer flush to next tick so current execution (e.g. error handler, toast) is never blocked
    setTimeout(flush, 0);
  } else {
    scheduleFlush();
  }
}

export const clientLogger = {
  log(message: string, context?: Record<string, unknown>) {
    enqueue('info', message, context);
  },
  info(message: string, context?: Record<string, unknown>) {
    enqueue('info', message, context);
  },
  warn(message: string, context?: Record<string, unknown>) {
    enqueue('warn', message, context);
  },
  error(message: string, context?: Record<string, unknown>) {
    enqueue('error', message, context);
  },
  debug(message: string, context?: Record<string, unknown>) {
    enqueue('debug', message, context);
  },
};
